/* eslint-disable */
import {
  PayloadAction, ThunkDispatch, createAsyncThunk, createSlice,
} from '@reduxjs/toolkit';
import { APIError, api, fetchDataUri } from '../app/helper';
import { fetchNextMatches } from './blobsSlice';
import { RootState } from '../app/store';

const sliceNameSpace = 'jobs_list' as const;

type TState = { status: 'idle' } | {
  status: 'success' | 'loading';
  reports: Record<string, IReport>; // key=job_id, value=IReport, Notice: job === report
  thumbnails: Record<string, string>; // key=file_id, value=file_as_string, because Blobs are not serializable
  activeReportId?: string;
} | {
  status: 'error';
  error: string;
};

export type TJobStatus = 'created' | 'running' | 'success' | 'error';

interface IResponse {
  status: string;
  message: string;
  data: IReportResponse[];
}

interface IReportResponse {
  _id: string;
  job_status: TJobStatus;
  pages_processed: number;
  total_pages: number;
  report_name: string;
  company: string;
  year: number;
  isPublic: boolean;
  en_doc: { id: string };
  de_doc: { id: string };
  doc_check?: {
    doc_en_check: IDocCheck,
    doc_de_check: IDocCheck
  };
}

export interface IDocCheck {
  status: boolean;
  msg: string;
  time_taken: number;
}

export interface IReport {
  job_id: string;
  job_status: TJobStatus;
  pages_processed: number;
  total_pages: number;
  report_name: string;
  company: string;
  year: number;
  isOwned: boolean; // if it is a public or private
  en_file_id: string; // id for the thumbnail as well
  de_file_id: string;
  doc_check?: {
    en: boolean;
    de: boolean;
  };
}

const initialState: TState = { status: 'idle' };

const getFileThumbnail = createAsyncThunk<{ job_id: string, imgUri: string }, { job_id: string, file_id: string }>(
  `${sliceNameSpace}/fetch_pdf_thumbnail`,
  ({ job_id, file_id }, { rejectWithValue }) => fetchDataUri(`/tcml/fetch_pdf_thumbnail/${file_id}`)
    .then((imgUri) => {
      if (!imgUri.startsWith('data:image/png;base64,')) throw new Error('not a thumbnail document');
      return { job_id, imgUri };
    }).catch(() => rejectWithValue(job_id)),
);

function parseReportResponse(
  reportsRes: IReportResponse[],
  dispatch: ThunkDispatch<unknown, unknown, any>,
): Record<string, IReport> {
  const reports: Record<string, IReport> = {};
  // eslint-disable-next-line no-restricted-syntax
  for (const report of reportsRes) {
    try {
      const {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        _id: job_id, en_doc: { id: en_file_id }, de_doc: { id: de_file_id }, isPublic, doc_check, ...rest
      } = report;
      reports[job_id] = {
        job_id, en_file_id, de_file_id, isOwned: !isPublic, ...rest,
        doc_check: doc_check && { en: doc_check.doc_en_check.status, de: doc_check.doc_de_check.status },
      };
      dispatch(getFileThumbnail({ job_id, file_id: en_file_id }));
    } catch (error) {
      console.warn(error, "Skipping this report:", report);
    }
  }
  return reports;
}

export const getReports = createAsyncThunk<Record<string, IReport>, void>(
  `${sliceNameSpace}/fetch_job_list`,
  async (_, { dispatch, rejectWithValue }) => api('/tcml/fetch_job_list')
    .then((response) => response.json())
    .then(async (json: IResponse) => {
      console.log(json.message);
      const reportsResponse = json.data;
      return parseReportResponse(reportsResponse, dispatch);
    }).catch((err) => {
      if (err instanceof APIError) {
        // user now should always have reports (public at least)
        // if (code === 404) return fulfillWithValue({}); // in case the user has no owned reports
        return rejectWithValue(err.details.message);
      }
      console.error(err);
      return rejectWithValue(err.message || 'Unknown error');
    }),
);

export const createReport = createAsyncThunk<{ job_id: string }, { formData: FormData }>(
  `${sliceNameSpace}/create`,
  ({ formData }) => api('/tcml/create_job', {
    method: 'POST',
    body: formData,
  })
    .then((response) => response.json())
    .then((json) => {
      if (json.data.job_id) {
        console.log({ json });
        return { job_id: json.data.job_id };
      }
      throw new Error("CreateJob didn't return a job_id!");
    })
    .catch((err: APIError) => {
      console.error(err);
      throw err;
    }),
);

const { actions, reducer } = createSlice({
  name: sliceNameSpace,
  initialState: initialState as TState,
  reducers: {
    setActiveReportId: (state, { payload: job_id }: PayloadAction<string>) => {
      if (state.status === 'success') state.activeReportId = job_id;
    },
    clear: () => ({ ...initialState }),
  },
  extraReducers: (builder) => {
    builder
      .addCase(getReports.pending, () => ({ status: 'loading', reports: {}, thumbnails: {} }))
      .addCase(getReports.fulfilled, (state, { payload }) => {
        if (state.status === 'loading') {
          state.status = 'success';
          state.reports = payload;
        }
      });
    // .addCase(getReports.rejected, (_, { payload }) => (
    //   { status: 'error', error: payload as string || 'unknown error' }
    // ));
    builder
      .addCase(getFileThumbnail.fulfilled, (state: TState, { payload: { job_id, imgUri } }) => {
        if (state.status === 'error') throw new Error('can not fetch thumbnails before the jobs/reports list?!');
        if (state.status !== 'idle') state.thumbnails[job_id] = imgUri;
      });
    builder
      // .addCase(fetchAllMatches.fulfilled, (state: TState, { payload: { job_status, pages_processed } }) => {
      //   if (state.status === 'success' && state.activeReportId) {
      //     state.reports[state.activeReportId].job_status = job_status;
      //     state.reports[state.activeReportId].pages_processed = pages_processed;
      //   }
      // })
      .addCase(fetchNextMatches.fulfilled, (state: TState, { payload: { job_status, pages_processed, doc_check } }) => {
        if (state.status === 'success' && state.activeReportId) {
          state.reports[state.activeReportId].job_status = job_status;
          state.reports[state.activeReportId].pages_processed = pages_processed;
          state.reports[state.activeReportId].doc_check = doc_check;
        }
      });
  },
});

export const selectReport = (state: RootState, job_id: string): IReport | undefined => {
  const slice = state[sliceNameSpace];
  if (job_id && slice.status === 'success') return slice.reports[job_id];
  return undefined;
};

export const selectActiveReport = (state: RootState): IReport | undefined => {
  const slice = state[sliceNameSpace];
  if (slice.status === 'success' && slice.activeReportId) return slice.reports[slice.activeReportId];
  return undefined;
};

export const selectThumbnail = (state: RootState, job_id: string): string | undefined => {
  const slice = state[sliceNameSpace];
  // eslint-disable-next-line max-len
  if (slice.status === 'success' && job_id in slice.reports) return slice.thumbnails[job_id];
  return undefined;
};

export const { setActiveReportId, clear } = actions;

export default { reducer, namespace: sliceNameSpace };
